import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Shield, FileDown, Folder, FileSpreadsheet, Activity, HeartPulse, FolderOpen, ClipboardList, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { exportTeamPdf, exportTeamXlsx } from "@/lib/export";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import Evaluations from "@/components/Evaluations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = [
  "Aficionados",
  "Juveniles",
  "Cadetes",
  "Infantiles",
  "Alevín F-11",
  "Alevín",
  "Benjamines",
  "Prebenjamines",
];
const TACTICAL_CATEGORIES = new Set(["Infantiles", "Cadetes", "Juveniles", "Aficionados"]);

const MODEL_KEYS = [
  { phase: "model_attack", obj: "model_attack_obj", label: "Ataque" },
  { phase: "model_defense", obj: "model_defense_obj", label: "Defensa" },
  { phase: "model_trans_atk_def", obj: "model_trans_atk_def_obj", label: "Transición ataque → defensa" },
  { phase: "model_trans_def_atk", obj: "model_trans_def_atk_obj", label: "Transición defensa → ataque" },
];

const STAFF_SLOTS = [
  { key: "coach_id", label: "Entrenador principal", testid: "team-coach-select" },
  { key: "assistant_coach_id", label: "Segundo entrenador (ayudante)", testid: "team-assistant-select" },
  { key: "fitness_coach_id", label: "Preparador físico", testid: "team-fitness-select" },
  { key: "delegate_id", label: "Delegado", testid: "team-delegate-select" },
];

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState({ from: "", to: "", phase: "PRESEASON" });
  const [editing, setEditing] = useState(null);
  const blankForm = () =>
    Object.fromEntries([
      ["name", ""], ["category", ""], ["standings_url", ""],
      ["preseason_start", ""], ["competition_start", ""],
      ...STAFF_SLOTS.map((s) => [s.key, "none"]),
      ...MODEL_KEYS.flatMap(({ phase, obj }) => [[phase, ""], [obj, ""]]),
    ]);
  const [form, setForm] = useState(blankForm());
  const [docsTeam, setDocsTeam] = useState(null);
  const [modelForm, setModelForm] = useState({});
  const [savingModel, setSavingModel] = useState(false);

  const canEditTeamDocs = (t) =>
    user?.role === "admin" ||
    user?.id === t?.coach_id ||
    user?.id === t?.assistant_coach_id ||
    user?.id === t?.fitness_coach_id ||
    user?.id === t?.delegate_id;

  const openDocs = (t) => {
    setDocsTeam(t);
    setModelForm(
      Object.fromEntries(
        MODEL_KEYS.flatMap(({ phase, obj }) => [
          [phase, t[phase] || ""],
          [obj, t[obj] || ""],
        ])
      )
    );
  };

  const saveModel = async () => {
    if (!docsTeam) return;
    setSavingModel(true);
    try {
      const updated = await api.put(`/teams/${docsTeam.id}/model`, modelForm);
      toast.success("Modelo de juego guardado");
      // refresh local list and current dialog data
      setTeams((xs) => xs.map((x) => (x.id === updated.data.id ? updated.data : x)));
      setDocsTeam(updated.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingModel(false);
    }
  };

  const load = () => api.get("/teams").then((r) => setTeams(r.data));

  useEffect(() => {
    load();
    if (user?.role === "admin") api.get("/users/coaches").then((r) => setCoaches(r.data));
  }, [user]);

  // For each slot, exclude users that are already picked in ANOTHER slot so
  // the same person can't hold two staff roles on the same team.
  const otherSelected = (currentKey) =>
    new Set(
      STAFF_SLOTS
        .filter((s) => s.key !== currentKey)
        .map((s) => form[s.key])
        .filter((v) => v && v !== "none"),
    );
  const coachesForSlot = (slotKey) => {
    const taken = otherSelected(slotKey);
    return coaches.filter((c) => !taken.has(c.id));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      category: t.category || "",
      standings_url: t.standings_url || "",
      preseason_start: t.preseason_start || "",
      competition_start: t.competition_start || "",
      ...Object.fromEntries(STAFF_SLOTS.map((s) => [s.key, t[s.key] || "none"])),
      ...Object.fromEntries(
        MODEL_KEYS.flatMap(({ phase, obj }) => [[phase, t[phase] || ""], [obj, t[obj] || ""]])
      ),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      standings_url: (form.standings_url || "").trim(),
      preseason_start: form.preseason_start || "",
      competition_start: form.competition_start || "",
      ...Object.fromEntries(
        STAFF_SLOTS.map((s) => [
          s.key,
          form[s.key] && form[s.key] !== "none" ? form[s.key] : null,
        ]),
      ),
      ...Object.fromEntries(
        MODEL_KEYS.flatMap(({ phase, obj }) => [[phase, form[phase] || ""], [obj, form[obj] || ""]])
      ),
    };
    try {
      if (editing) await api.put(`/teams/${editing.id}`, payload);
      else await api.post("/teams", payload);
      toast.success(editing ? "Equipo actualizado" : "Equipo creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar "${t.name}"? Se eliminarán sus jugadores.`)) return;
    await api.delete(`/teams/${t.id}`);
    toast.success("Equipo eliminado");
    load();
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>
            Equipos
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin ? "Gestiona todos los equipos del club." : "Tu equipo asignado."}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-team-button">
                <Plus className="h-4 w-4 mr-2" /> Nuevo equipo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Editar equipo" : "Nuevo equipo"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name-input" />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={form.category || "none"} onValueChange={(v) => setForm({ ...form, category: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="team-category-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin categoría</SelectItem>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>URL clasificación (RFFM)</Label>
                  <Input
                    type="url"
                    value={form.standings_url}
                    onChange={(e) => setForm({ ...form, standings_url: e.target.value })}
                    placeholder="https://www.rffm.es/competicion/clasificaciones..."
                    data-testid="team-standings-url-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Inicio pretemporada</Label>
                    <Input type="date" value={form.preseason_start}
                      onChange={(e) => setForm({ ...form, preseason_start: e.target.value })}
                      data-testid="team-preseason-start-input" />
                  </div>
                  <div>
                    <Label>Inicio competición</Label>
                    <Input type="date" value={form.competition_start}
                      onChange={(e) => setForm({ ...form, competition_start: e.target.value })}
                      data-testid="team-competition-start-input" />
                  </div>
                  <p className="col-span-2 text-[10px] text-slate-400 -mt-2">
                    Al guardar, los entrenamientos y partidos sin fase manual se clasifican automáticamente como Pretemporada o Competición según estas fechas.
                  </p>
                </div>
                {editing && (
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="bulk-phase-tool">
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">Asignar fase por rango (edición masiva)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="date" value={bulk.from} onChange={(e) => setBulk({ ...bulk, from: e.target.value })} data-testid="bulk-phase-from" />
                      <Input type="date" value={bulk.to} onChange={(e) => setBulk({ ...bulk, to: e.target.value })} data-testid="bulk-phase-to" />
                    </div>
                    <div className="flex gap-2">
                      <select value={bulk.phase} onChange={(e) => setBulk({ ...bulk, phase: e.target.value })}
                        className="flex-1 h-9 rounded-md border border-slate-200 text-sm px-2" data-testid="bulk-phase-value">
                        <option value="PRESEASON">Pretemporada</option>
                        <option value="COMPETITION">Competición</option>
                      </select>
                      <Button type="button" variant="outline" size="sm" className="font-bold" data-testid="bulk-phase-apply"
                        onClick={async () => {
                          if (!bulk.from || !bulk.to) { toast.error("Indica el rango de fechas"); return; }
                          try {
                            const { data } = await api.post(`/teams/${editing.id}/season-phase/bulk`, {
                              date_from: bulk.from, date_to: bulk.to, season_phase: bulk.phase, scope: "both",
                            });
                            toast.success(`Fase asignada: ${data.trainings} entrenos y ${data.matches} partidos`);
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}>
                        Aplicar
                      </Button>
                    </div>
                  </div>
                )}
                {STAFF_SLOTS.map((slot) => (
                  <div key={slot.key}>
                    <Label>{slot.label}</Label>
                    <Select
                      value={form[slot.key]}
                      onValueChange={(v) => setForm({ ...form, [slot.key]: v })}
                    >
                      <SelectTrigger data-testid={slot.testid}>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {coachesForSlot(slot.key).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} — {c.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}

                {TACTICAL_CATEGORIES.has(form.category) && (
                  <div className="text-xs text-purple-700 italic pt-2 border-t border-slate-200">
                    El <span className="font-bold">Modelo de juego</span> y sus documentos
                    se editan desde el icono de carpeta en la tarjeta del equipo.
                  </div>
                )}

                <DialogFooter>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="team-save-button">Guardar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="equipos">
        <TabsList>
          <TabsTrigger value="equipos" data-testid="tab-equipos">
            <Shield className="h-4 w-4 mr-1.5" /> Equipos
          </TabsTrigger>
          <TabsTrigger value="documentacion" data-testid="tab-documentacion">
            <FolderOpen className="h-4 w-4 mr-1.5" /> Documentación
          </TabsTrigger>
          <TabsTrigger value="informes" data-testid="tab-informes">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Informes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="equipos">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.length === 0 && (
              <div className="col-span-full text-slate-500 text-sm">Aún no hay equipos.</div>
            )}
            {teams.map((t) => (
              <Card key={t.id} className="border-slate-200 shadow-sm hover:-translate-y-1 transition-transform min-w-0" data-testid={`team-card-${t.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-800 grid place-items-center">
                  <Shield className="h-5 w-5" />
                </div>
                <div className="flex flex-wrap justify-end gap-1 min-w-0">
                  {t.standings_url && (
                    <a href={t.standings_url} target="_blank" rel="noreferrer" title="Ver clasificación">
                      <Button variant="ghost" size="icon" data-testid={`standings-team-${t.id}`}>
                        <Trophy className="h-4 w-4 text-amber-500" />
                      </Button>
                    </a>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => exportTeamPdf(t)} data-testid={`export-team-${t.id}`} title="Exportar PDF">
                    <FileDown className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => exportTeamXlsx(t)} data-testid={`export-team-xlsx-${t.id}`} title="Exportar Excel">
                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  </Button>
                  <Link to={`/teams/${t.id}/fisico`} title="Tests físicos">
                    <Button variant="ghost" size="icon" data-testid={`physical-team-${t.id}`}>
                      <Activity className="h-4 w-4 text-purple-600" />
                    </Button>
                  </Link>
                  <Link to={`/teams/${t.id}/cargas`} title="Cargas & Wellness">
                    <Button variant="ghost" size="icon" data-testid={`cargas-team-${t.id}`}>
                      <HeartPulse className="h-4 w-4 text-rose-500" />
                    </Button>
                  </Link>
                  {TACTICAL_CATEGORIES.has(t.category) && canEditTeamDocs(t) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDocs(t)}
                      title="Modelo de juego"
                      data-testid={`docs-team-${t.id}`}
                    >
                      <Folder className="h-4 w-4 text-purple-600" />
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-team-${t.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-team-${t.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-xl font-black" style={{ fontFamily: "Nunito" }}>{t.name}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                {t.category || "Sin categoría"}
              </div>
              {t.standings_url && (
                <a
                  href={t.standings_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-bold hover:bg-amber-100 transition"
                  data-testid={`standings-link-${t.id}`}
                >
                  <Trophy className="h-3 w-3" /> Clasificación
                </a>
              )}
              <div className="mt-3 text-sm text-slate-600 space-y-1">
                <div>
                  <span className="text-slate-400">Entrenador:</span>{" "}
                  <span className="font-semibold">{t.coach_name || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Ayudante:</span>{" "}
                  <span className="font-semibold">{t.assistant_coach_name || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Preparador físico:</span>{" "}
                  <span className="font-semibold">{t.fitness_coach_name || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Delegado:</span>{" "}
                  <span className="font-semibold">{t.delegate_name || "—"}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
          </div>
        </TabsContent>

        <TabsContent value="documentacion">
          <DocumentationTab teams={teams} canEdit={canEditTeamDocs} />
        </TabsContent>

        <TabsContent value="informes">
          <Evaluations teams={teams} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!docsTeam} onOpenChange={(o) => !o && setDocsTeam(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Modelo de juego — {docsTeam?.name}
            </DialogTitle>
          </DialogHeader>
          {docsTeam && (
            <div className="space-y-5">
              <p className="text-xs text-slate-500">
                Define las cuatro fases del modelo de juego y adjunta los documentos
                de apoyo (presentaciones, fichas tácticas, etc.).
              </p>

              <div className="space-y-4">
                {MODEL_KEYS.map(({ phase, obj, label }) => (
                  <div key={phase} className="space-y-1.5">
                    <Label className="text-sm font-bold text-purple-800">{label}</Label>
                    <Textarea
                      rows={3}
                      value={modelForm[phase] || ""}
                      onChange={(e) => setModelForm({ ...modelForm, [phase]: e.target.value })}
                      disabled={!canEditTeamDocs(docsTeam)}
                      placeholder={`Describe la fase de ${label.toLowerCase()}...`}
                      data-testid={`model-${phase}`}
                    />
                    <Label className="text-xs uppercase tracking-widest text-slate-500 pt-1">
                      Objetivo
                    </Label>
                    <Input
                      value={modelForm[obj] || ""}
                      onChange={(e) => setModelForm({ ...modelForm, [obj]: e.target.value })}
                      disabled={!canEditTeamDocs(docsTeam)}
                      placeholder="Objetivo de esta fase"
                      data-testid={`model-${obj}`}
                    />
                  </div>
                ))}
              </div>

              {canEditTeamDocs(docsTeam) && (
                <div className="flex justify-end">
                  <Button
                    onClick={saveModel}
                    disabled={savingModel}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                    data-testid="save-model-button"
                  >
                    {savingModel ? "Guardando..." : "Guardar modelo"}
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t border-slate-100">
                <AttachmentsPanel
                  scope="team_model"
                  scopeId={docsTeam.id}
                  canEdit={canEditTeamDocs(docsTeam)}
                  title="Documentos del modelo de juego"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Documentation tab (inside /teams) ----------
const DOC_ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt";
const DOC_ALLOWED_EXT = new Set([
  "jpg", "jpeg", "png", "pdf", "doc", "docx", "ppt", "pptx",
  "xls", "xlsx", "csv", "txt",
]);
const DOC_MAX_FILES = 30;

function DocumentationTab({ teams, canEdit }) {
  const [categories, setCategories] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  useEffect(() => {
    api.get("/team-doc-categories")
      .then((r) => setCategories(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!selectedTeamId && teams.length > 0) setSelectedTeamId(teams[0].id);
  }, [teams, selectedTeamId]);

  const team = teams.find((t) => t.id === selectedTeamId);
  const editable = team ? canEdit(team) : false;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Nunito" }}>
            Documentación del equipo
          </h2>
          <p className="text-sm text-slate-500">
            Reglamento, metodología, partes de lesión, material y normas de la temporada.
          </p>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</Label>
          <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
            <SelectTrigger className="min-w-[240px]" data-testid="docs-team-select">
              <SelectValue placeholder="Selecciona equipo" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!team ? (
        <div className="text-sm text-slate-400 italic">Selecciona un equipo.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {categories.map((c) => (
            <Card key={c.key} className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Folder className="h-4 w-4 text-purple-600" />
                  <h3 className="font-bold text-slate-800">{c.label}</h3>
                </div>
                <AttachmentsPanel
                  scope="team_doc"
                  scopeId={`${team.id}::${c.key}`}
                  canEdit={editable}
                  title=""
                  maxFiles={DOC_MAX_FILES}
                  accept={DOC_ACCEPT}
                  allowedExt={DOC_ALLOWED_EXT}
                  hint={`PDF, Word, Excel, PowerPoint, imágenes · máx. 10 MB · hasta ${DOC_MAX_FILES} archivos`}
                  emptyLabel="Aún no hay documentos en esta sección."
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

